import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../../core/tools/interfaces/ITool';
import { ICommand } from '../../../core/commands/ICommand';
import { UpdateEdgeCommand } from '../../../core/commands/edges/UpdateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../../components/ui/collapsible';
import { RelationshipSubtypeSwitcher } from '../../../components/ui/RelationshipSubtypeSwitcher';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

export class ConnectorEditTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-connector-edit',
    name: i18n.t('genogram:tools.editConnector', { defaultValue: 'Edit Connector' }) as string,
    icon: 'link',
    category: 'edit',
    description: 'Change connector type and properties',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram'],
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: { toolbar: false, contextMenu: false, propertyPanel: true, sidebar: false, floatingPanel: false, statusBar: false },
    applicability: {
      diagramTypes: ['genogram'],
      contextTypes: ['connector'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: true,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.editConnector', 'Edit Connector');
  }

  canExecute(context: IToolContext): boolean {
    if (context.diagramType !== 'genogram' || context.selection.size !== 1) {
      return false;
    }

    // Exclude free lines (new Line shapes) - they don't need the relationship library panel
    // But allow existing connector lines (created from shape connection points)
    const selectedId = Array.from(context.selection)[0];
    const edges = useDiagramStore.getState().edges;
    const edge = edges.find(e => e.id === selectedId);
    if (edge) {
      // Check if it's a free line (new Line shape from shape picker)
      const isFreeLine = edge.type === 'line' && 
        edge.from?.startsWith('__freeF:') && 
        edge.to?.startsWith('__freeT:') &&
        (edge.attrs as any)?.fromPoint &&
        (edge.attrs as any)?.toPoint;
      if (isFreeLine) {
        return false;
      }
    }

    return true;
  }

  // Single unified execute that accepts:
  // - a batch of commands (array) and returns them
  // - an object { edgeId, updates } to create an UpdateEdgeCommand
  execute(params?: any): ICommand | ICommand[] | null {
    if (!params) return null;
    if (Array.isArray(params)) return params;
    if (params.edgeId && params.updates) {
      return new UpdateEdgeCommand(params.edgeId, params.updates);
    }
    return null;
  }

  getPropertyPanelComponent() {
    const Panel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
      const ids = Array.from(context.selection);
      const edges = useDiagramStore(s => s.edges);
      if (ids.length !== 1) return null;
      const edge = edges.find(e => e.id === ids[0]);
      if (!edge) return null;
      const attrs = (edge as any).attrs || {};
      const set = (updates: any) => onExecute(new UpdateEdgeCommand(edge.id, { attrs: { ...(edge as any).attrs, ...updates } }));
      const setLabel = (nextValue: string) => {
        const shouldHideCaption = nextValue.trim().length === 0;
        set({
          label: shouldHideCaption ? undefined : nextValue,
          __captionHidden: shouldHideCaption ? true : false,
        });
      };

      const primary: Array<{ key: string; label: string; control: React.ReactNode; width?: 'full'|'half' }> = [
        {
          key: 'color',
          label: 'Color',
          control: (
            <Input
              type="color"
              value={(attrs.color as string) || '#334155'}
              onChange={(e) => set({ color: e.target.value })}
              className="h-8 w-16 p-1"
            />
          ),
          width: 'half'
        },
        {
          key: 'width',
          label: 'Width',
          control: (
            <Input
              type="number"
              value={typeof attrs.width === 'number' ? String(attrs.width) : '2'}
              onChange={(e) => set({ width: Number(e.target.value || 2) })}
              className="h-8 text-sm"
            />
          ),
          width: 'half'
        },
        {
          key: 'routingType',
          label: 'Routing',
          control: (
            <ToggleGroup type="single" value={attrs.routingType || 'bezier'} onValueChange={(v) => set({ routingType: v })}>
              <ToggleGroupItem value="straight">Straight</ToggleGroupItem>
              <ToggleGroupItem value="orthogonal">Orthogonal</ToggleGroupItem>
              <ToggleGroupItem value="orthogonalRounded">Orthogonal (Rounded)</ToggleGroupItem>
              <ToggleGroupItem value="bezier">Bezier</ToggleGroupItem>
            </ToggleGroup>
          ),
          width: 'full'
        },
      ];

      const secondary: Array<{ key: string; label: string; control: React.ReactNode }> = [
        {
          key: 'endArrow',
          label: 'Arrowhead',
          control: (
            <Select value={(attrs.endArrow?.type as string) || 'triangle'} onValueChange={(v) => set({ endArrow: { ...(attrs.endArrow || {}), type: v } })}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['none','triangle','circle','diamond','bar'].map(v => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          )
        },
        {
          key: 'dashed',
          label: 'Dashed',
          control: (
            <ToggleGroup type="single" value={attrs.dashed ? 'yes' : 'no'} onValueChange={(v) => set({ dashed: v === 'yes' })}>
              <ToggleGroupItem value="no">Solid</ToggleGroupItem>
              <ToggleGroupItem value="yes">Dashed</ToggleGroupItem>
            </ToggleGroup>
          )
        },
        {
          key: 'label',
          label: 'Label',
          control: (
            <Input
              value={(attrs.label as string) || ''}
              onChange={(e) => setLabel(e.target.value)}
              className="h-8 text-sm"
            />
          )
        }
      ];

      return (
        <div className="space-y-4">
          <RelationshipSubtypeSwitcher
            diagramType={'genogram' as any}
            selectedEdgeIds={[edge.id]}
            onExecute={(cmds) => onExecute(cmds)}
          />
          <div className="grid grid-cols-2 gap-3">
            {primary.map(f => (
              <div key={f.key} className={f.width === 'full' ? 'col-span-2 space-y-1' : 'space-y-1'}>
                <Label className="text-xs text-muted-foreground">{f.label}</Label>
                {f.control}
              </div>
            ))}
          </div>

          {secondary.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger className="text-xs text-blue-600 hover:underline">More</CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-3">
                {secondary.map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{f.label}</Label>
                    {f.control}
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      );
    };
    return Panel;
  }

}
